import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Button,
  Container,
  Typography,
  Box,
  Card,
  CircularProgress,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  Paper,
  Chip,
  Divider,
  Fab,
  IconButton
} from '@mui/material';
import { DndContext, DragOverlay, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import HotelIcon from '@mui/icons-material/Hotel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ArchiveIcon from '@mui/icons-material/Archive';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import FlagIcon from '@mui/icons-material/Flag';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CommentIcon from '@mui/icons-material/Comment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import { getApi, patchApi } from '../services/api';
import AddTaskDialog from './AddTaskDialog';

// Componente para tarea individual en el Kanban
const TaskCard = ({ task, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const priorityConfig = {
    low: { color: '#4caf50', label: 'Baja' },
    medium: { color: '#ff9800', label: 'Media' },
    high: { color: '#f44336', label: 'Alta' },
    urgent: { color: '#9c27b0', label: 'Urgente' }
  };

  const priority = priorityConfig[task.priority] || { color: '#9e9e9e', label: 'Media' };

  const dueDate = task.dueDate ? new Date(task.dueDate) : null;
  const now = new Date();
  const isOverdue = dueDate && dueDate < now && task.status !== 'done';
  const isDueSoon = dueDate && dueDate.getTime() - now.getTime() < 24 * 60 * 60 * 1000 && dueDate > now;

  const handleEdit = (e) => {
    e.stopPropagation(); // Prevent drag from triggering
    onEdit(task);
  };

  const handleDoubleClick = () => {
    onEdit(task);
  };

  return (
    <Card
      style={style}
      sx={{
        p: 2,
        mb: 1,
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
        border: '1px solid #e0e0e0',
        '&:hover': { boxShadow: 2 },
        position: 'relative'
      }}
    >
      {/* Edit button in top-right corner - outside drag area */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
          pointerEvents: 'auto'
        }}
      >
        <IconButton
          size="small"
          onClick={handleEdit}
          onPointerDown={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)'
            },
            pointerEvents: 'auto'
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Draggable content area */}
      <Box
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        onDoubleClick={handleDoubleClick}
        sx={{
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          pointerEvents: 'auto'
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ pr: 4 }}>
          {task.title}
        </Typography>

        {task.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {task.description.length > 100 ? `${task.description.substring(0, 100)}...` : task.description}
          </Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Chip label={priority.label} size="small" sx={{ backgroundColor: priority.color, color: 'white', fontSize: '0.7rem' }} />
          {task.tags?.slice(0, 2).map((tag, index) => (
            <Chip key={index} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
          ))}
        </Stack>

        {dueDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CalendarTodayIcon
              sx={{ fontSize: 16, mr: 0.5, color: isOverdue ? 'error.main' : isDueSoon ? 'warning.main' : 'text.secondary' }}
            />
            <Typography variant="caption" color={isOverdue ? 'error.main' : isDueSoon ? 'warning.main' : 'text.secondary'}>
              {dueDate.toLocaleDateString()}
            </Typography>
          </Box>
        )}

        {task.assigneeDetails && task.assigneeDetails.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PersonIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {task.assigneeDetails
                .slice(0, 2)
                .map((a) => `${a.firstName} ${a.lastName}`)
                .join(', ')}
              {task.assigneeDetails.length > 2 && ` +${task.assigneeDetails.length - 2}`}
            </Typography>
          </Box>
        )}

        {task.checklist && task.checklist.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {task.checklist.filter((item) => item.done).length}/{task.checklist.length}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

// Componente para columna del Kanban
const KanbanColumn = ({ id, title, icon, tasks, color, onEdit, onDelete }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: id
  });

  return (
    <Paper
      ref={setNodeRef}
      sx={{
        p: 2,
        minHeight: 500,
        backgroundColor: isOver ? `${color}10` : '#f8f9fa',
        border: `2px solid ${isOver ? color : `${color}20`}`,
        transition: 'all 0.2s ease'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon}
        <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Chip
          label={tasks.length}
          size="small"
          sx={{
            ml: 'auto',
            backgroundColor: color,
            color: 'white'
          }}
        />
      </Box>

      <SortableContext items={tasks.map((t) => t._id)} strategy={verticalListSortingStrategy}>
        {tasks.map((task) => (
          <TaskCard key={task._id} task={task} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </SortableContext>
    </Paper>
  );
};

const TasksPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estados
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Stats
  const [statsData, setStatsData] = useState({
    open: 0,
    in_progress: 0,
    done: 0,
    archived: 0
  });

  // Obtener datos del hotel del localStorage
  const hotel = JSON.parse(localStorage.getItem('hotelData')) || {};

  // Configuración de drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // Handlers para modales
  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (task) => {
    setEditingTask(task);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditingTask(null);
  };

  // Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Mostrar mensaje
  const showMessage = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Fetching de datos
  const fetchTasks = useCallback(async () => {
    if (!hotel?.hotelId) {
      setError('No se encontraron datos del hotel. Por favor, inicie sesión nuevamente.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getApi(`api/tasks?hotelId=${hotel.hotelId}`);
      const tasksData = response?.data?.tasks || [];
      setTasks(tasksData);

      // Calcular estadísticas
      const stats = tasksData.reduce(
        (acc, task) => {
          acc[task.status] = (acc[task.status] || 0) + 1;
          return acc;
        },
        { open: 0, in_progress: 0, done: 0, archived: 0 }
      );

      setStatsData(stats);
    } catch (error) {
      setError('Error al cargar las tareas. Por favor, intente nuevamente.');
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [hotel.hotelId]);

  // Efecto para cargar datos
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Función para refrescar datos después de añadir/editar una tarea
  const handleTaskSaved = () => {
    handleCloseAdd();
    handleCloseEdit();
    fetchTasks();
    showMessage('Tarea guardada exitosamente', 'success');
  };

  // Handle delete task
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta tarea?')) return;

    try {
      await patchApi(`api/tasks/${taskId}`, { isDeleted: true });
      fetchTasks();
      showMessage('Tarea eliminada exitosamente', 'success');
    } catch (error) {
      showMessage('Error al eliminar la tarea', 'error');
      console.error('Error deleting task:', error);
    }
  };

  // Handle drag end - actualizar estado de la tarea
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) return;

    const taskId = active.id;
    let newStatus;

    // Check if dropped over a column or another task
    const columnIds = ['open', 'in_progress', 'done', 'archived'];
    if (columnIds.includes(over.id)) {
      // Dropped over a column
      newStatus = over.id;
    } else {
      // Dropped over another task - find the column it belongs to
      const targetTask = tasks.find((t) => t._id === over.id);
      if (targetTask) {
        newStatus = targetTask.status;
      } else {
        return; // Invalid drop target
      }
    }

    // Encontrar la tarea
    const task = tasks.find((t) => t._id === taskId);
    if (!task || task.status === newStatus) return;

    try {
      // Actualizar en el backend
      await patchApi(`api/tasks/${taskId}`, { status: newStatus });

      // Actualizar localmente
      setTasks((prevTasks) => prevTasks.map((t) => (t._id === taskId ? { ...t, status: newStatus } : t)));

      // Recalcular estadísticas
      setStatsData((prevStats) => {
        const newStats = { ...prevStats };
        newStats[task.status]--;
        newStats[newStatus]++;
        return newStats;
      });

      showMessage('Tarea movida exitosamente', 'success');
    } catch (error) {
      showMessage('Error al mover la tarea', 'error');
      console.error('Error updating task status:', error);
      // Revertir cambios locales en caso de error
      fetchTasks();
    }
  };

  // Organizar tareas por estado
  const tasksByStatus = {
    open: tasks.filter((task) => task.status === 'open'),
    in_progress: tasks.filter((task) => task.status === 'in_progress'),
    done: tasks.filter((task) => task.status === 'done'),
    archived: tasks.filter((task) => task.status === 'archived')
  };

  // Configuración de columnas
  const columns = [
    {
      id: 'open',
      title: 'Abiertas',
      icon: <CheckCircleOutlineIcon sx={{ color: '#2196f3' }} />,
      color: '#2196f3',
      tasks: tasksByStatus.open
    },
    {
      id: 'in_progress',
      title: 'En Progreso',
      icon: <PendingIcon sx={{ color: '#ff9800' }} />,
      color: '#ff9800',
      tasks: tasksByStatus.in_progress
    },
    {
      id: 'done',
      title: 'Completadas',
      icon: <CheckCircleIcon sx={{ color: '#4caf50' }} />,
      color: '#4caf50',
      tasks: tasksByStatus.done
    },
    {
      id: 'archived',
      title: 'Archivadas',
      icon: <ArchiveIcon sx={{ color: '#9e9e9e' }} />,
      color: '#9e9e9e',
      tasks: tasksByStatus.archived
    }
  ];

  return (
    <>
      {/* Modal para añadir tarea */}
      <AddTaskDialog open={openAdd} handleClose={handleCloseAdd} onSuccess={handleTaskSaved} />

      {/* Modal para editar tarea */}
      <AddTaskDialog open={openEdit} handleClose={handleCloseEdit} onSuccess={handleTaskSaved} editingTask={editingTask} />

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Container maxWidth="xl">
        {/* Panel de estadísticas */}
        <Box mb={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '16px',
              background: 'linear-gradient(to right, #f5f7fa, #ffffff)',
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
              overflow: 'hidden'
            }}
          >
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} divider={!isMobile && <Divider orientation="vertical" flexItem />}>
              {/* Título y resumen */}
              <Box flex={1}>
                <Typography variant="h4" color="primary" gutterBottom>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    Gestión de Tareas
                  </Box>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Administra todas las tareas operativas de tu hotel en un solo lugar.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAdd}
                  sx={{
                    borderRadius: '20px',
                    mt: 1,
                    boxShadow: '0 4px 12px 0 rgba(0,0,0,0.1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px 0 rgba(0,0,0,0.15)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  Nueva Tarea
                </Button>
              </Box>

              {/* Tarjetas de estadísticas */}
              <Stack direction={isMobile ? 'column' : 'row'} spacing={2} flex={2} width="100%">
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    flex: 1,
                    borderRadius: '12px',
                    bgcolor: '#e3f2fd',
                    border: '1px solid #bbdefb',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 40, color: '#2196f3', mb: 1 }} />
                  <Typography variant="h4" align="center" color="#2196f3">
                    {statsData.open}
                  </Typography>
                  <Typography variant="subtitle2" align="center" color="#2196f3">
                    Abiertas
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    flex: 1,
                    borderRadius: '12px',
                    bgcolor: '#fff8e1',
                    border: '1px solid #ffecb3',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PendingIcon sx={{ fontSize: 40, color: '#f57c00', mb: 1 }} />
                  <Typography variant="h4" align="center" color="#f57c00">
                    {statsData.in_progress}
                  </Typography>
                  <Typography variant="subtitle2" align="center" color="#f57c00">
                    En Progreso
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    flex: 1,
                    borderRadius: '12px',
                    bgcolor: '#e8f5e9',
                    border: '1px solid #c8e6c9',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
                  <Typography variant="h4" align="center" color="#2e7d32">
                    {statsData.done}
                  </Typography>
                  <Typography variant="subtitle2" align="center" color="#2e7d32">
                    Completadas
                  </Typography>
                </Paper>
              </Stack>
            </Stack>
          </Paper>
        </Box>

        {/* Kanban Board */}
        <Box width="100%">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && !tasks.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
              <Stack spacing={2} alignItems="center">
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Cargando tareas...
                </Typography>
              </Stack>
            </Box>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  pb: 2,
                  minHeight: '70vh'
                }}
              >
                {columns.map((column) => (
                  <Box
                    key={column.id}
                    sx={{
                      minWidth: 300,
                      flex: 1
                    }}
                  >
                    <KanbanColumn
                      id={column.id}
                      title={column.title}
                      icon={column.icon}
                      tasks={column.tasks}
                      color={column.color}
                      onEdit={handleOpenEdit}
                      onDelete={handleDeleteTask}
                    />
                  </Box>
                ))}
              </Box>

              <DragOverlay>{/* Optional: Add drag overlay styling */}</DragOverlay>
            </DndContext>
          )}
        </Box>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add task"
          onClick={handleOpenAdd}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000
          }}
        >
          <AddIcon />
        </Fab>
      </Container>
    </>
  );
};

export default TasksPage;
